import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert, Box, Typography } from '@mui/material';
import QuizLayout from '../../../../shared/reusables/QuizLayout';
import QuizCard from '../../../../shared/reusables/QuizCard';
import QuizEndScreen from '../../../../shared/reusables/QuizEndScreen';
import { fetchWords } from '../../../../shared/api/darija';

const shuffleArray = (arr) => [...arr].sort(() => Math.random() - 0.5);

/**
 * VerbsBaseMode — quiz mode for verb base forms (darijaBase).
 * Kept separate from GenericQuizMode because it uses "darijaBase" instead of "darija".
 */
export default function VerbsBaseMode({ onBack }) {
    const [data, setData] = useState([]);
    const [order, setOrder] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [input, setInput] = useState('');
    const [showAnswer, setShowAnswer] = useState(false);
    const [messages, setMessages] = useState([]);
    const [progress, setProgress] = useState([]);
    const [quizFinished, setQuizFinished] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchWords('verbs')
            .then((verbs) => {
                const shuffled = shuffleArray(verbs.map((_, i) => i));
                setData(verbs);
                setOrder(shuffled);
                setProgress(shuffled.map(() => ({ correct: false, revealed: false })));
            })
            .catch((err) => {
                console.error('[VerbsBaseMode]:', err);
                setError(err.message || 'Ophalen mislukt.');
            })
            .finally(() => setLoading(false));
    }, []);

    const currentVerb = order.length ? data[order[currentIndex]] : null;

    const addMessage = useCallback(
        (type, text) => setMessages((prev) => [...prev, { type, text, id: Date.now() }]),
        []
    );
    const handleCloseMessage = useCallback(
        (id) => setMessages((prev) => prev.filter((m) => m.id !== id)),
        []
    );

    const nextWord = useCallback(() => {
        if (currentIndex + 1 < order.length) {
            setCurrentIndex((i) => i + 1);
            setInput('');
            setShowAnswer(false);
        } else {
            setQuizFinished(true);
        }
    }, [currentIndex, order.length]);

    const handleSubmit = useCallback(() => {
        if (!currentVerb) return;

        if (progress[currentIndex]?.revealed) {
            addMessage('error', '❌ Antwoord laten zien telt als fout.');
            setTimeout(nextWord, 400);
            return;
        }

        const isCorrect = input.trim().toLowerCase() === currentVerb.darijaBase?.toLowerCase();

        if (isCorrect) {
            addMessage('success', '✅ Goed gedaan!');
            setProgress((prev) => {
                const next = [...prev];
                next[currentIndex] = { correct: true, revealed: false };
                return next;
            });
            setTimeout(nextWord, 500);
        } else {
            addMessage('error', '❌ Fout! Probeer opnieuw.');
        }
    }, [currentVerb, progress, currentIndex, input, addMessage, nextWord]);

    const handleShowAnswer = useCallback(() => {
        setShowAnswer(true);
        setProgress((prev) => {
            const next = [...prev];
            next[currentIndex] = { correct: false, revealed: true };
            return next;
        });
    }, [currentIndex]);

    const resetQuiz = useCallback(() => {
        const shuffled = shuffleArray(data.map((_, i) => i));
        setOrder(shuffled);
        setProgress(shuffled.map(() => ({ correct: false, revealed: false })));
        setCurrentIndex(0);
        setInput('');
        setShowAnswer(false);
        setQuizFinished(false);
        setMessages([]);
    }, [data]);

    if (loading) return null;

    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: '#f87171', fontFamily: "'DM Sans', sans-serif" }}>
                    ⚠️ {error}
                </Typography>
            </Box>
        );
    }

    if (quizFinished) {
        const correctCount = progress.filter((p) => p.correct).length;
        const wrongItems = progress
            .map((p, i) => ({ ...p, dutch: data[order[i]]?.dutch, answer: data[order[i]]?.darijaBase }))
            .filter((p) => !p.correct)
            .map((w) => ({ question: w.dutch, answer: w.answer }));
        return (
            <QuizEndScreen
                correct={correctCount}
                total={order.length}
                wrongItems={wrongItems}
                onRetry={resetQuiz}
            />
        );
    }

    if (!currentVerb) return null;

    return (
        <>
            <QuizLayout current={currentIndex + 1} total={order.length} label="Werkwoorden" onBack={onBack}>
                <QuizCard
                    question={`Wat is "${currentVerb.dutch}"?`}
                    accentWord={currentVerb.dutch}
                    questionIndex={currentIndex}
                    input={input}
                    onInputChange={setInput}
                    onSubmit={handleSubmit}
                    onNext={nextWord}
                    onShowAnswer={handleShowAnswer}
                    showAnswer={showAnswer}
                    answer={currentVerb.darijaBase}
                />
            </QuizLayout>

            {messages.map((msg) => (
                <Snackbar
                    key={msg.id}
                    open
                    autoHideDuration={1500}
                    onClose={() => handleCloseMessage(msg.id)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => handleCloseMessage(msg.id)}
                        severity={msg.type}
                        sx={{ width: '100%', minWidth: 250 }}
                    >
                        {msg.text}
                    </Alert>
                </Snackbar>
            ))}
        </>
    );
}
